/**
 * UserDetailModal - Modal showing user details and actions
 */
export default function UserDetailModal({
  show,
  onClose,
  selectedUser,
  onShowGrantModal,
  onShowRefundModal,
  onShowDeductModal,
  onShowPlanModal,
  onShowImpersonateModal,
  onShowDeleteModal,
  onShowEnableModal,
}) {
  if (!show || !selectedUser) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{selectedUser.user.name}</h2>
            <p className="text-muted-foreground">{selectedUser.user.email}</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* User Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-muted-foreground text-sm">Plan</p>
              <p className="text-foreground font-semibold capitalize">{selectedUser.user.plan_type}</p>
            </div>
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-muted-foreground text-sm">Status</p>
              <p className="text-foreground font-semibold capitalize">{selectedUser.user.subscription_status}</p>
            </div>
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-muted-foreground text-sm">Credits</p>
              <p className="text-foreground font-semibold">{selectedUser.user.credits_remaining || 0}</p>
            </div>
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-muted-foreground text-sm">Questions</p>
              <p className="text-foreground font-semibold">{selectedUser.usage_stats.questions_asked}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={onShowImpersonateModal}
              data-testid="impersonate-user-btn"
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Impersonate User
            </button>
            <button
              onClick={onShowGrantModal}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-foreground rounded-lg transition"
            >
              Grant Credits
            </button>
            <button
              onClick={onShowRefundModal}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-foreground rounded-lg transition"
            >
              Issue Refund
            </button>
            <button
              onClick={onShowDeductModal}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-foreground rounded-lg transition"
            >
              Deduct Credits
            </button>
            <button
              onClick={onShowPlanModal}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-foreground rounded-lg transition"
            >
              Change Plan
            </button>
            {selectedUser.user.disabled ? (
              <button
                onClick={onShowEnableModal}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-foreground rounded-lg transition"
              >
                Enable User
              </button>
            ) : (
              <button
                onClick={onShowDeleteModal}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-foreground rounded-lg transition"
              >
                Delete User
              </button>
            )}
          </div>

          {/* Credit History */}
          <div>
            <h3 className="text-lg font-bold text-foreground mb-4">Credit History</h3>
            <div className="bg-muted rounded-lg divide-y divide-slate-700 max-h-60 overflow-y-auto">
              {selectedUser.credit_history.length === 0 ? (
                <p className="p-4 text-muted-foreground text-center">No credit history</p>
              ) : (
                selectedUser.credit_history.map((log, idx) => (
                  <div key={idx} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-foreground font-medium">{log.reason}</p>
                      <p className="text-muted-foreground text-sm">
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-bold ${
                          log.amount_deducted < 0 ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {log.amount_deducted < 0 ? "+" : "-"}
                        {Math.abs(log.amount_deducted)}
                      </p>
                      <p className="text-muted-foreground text-sm">{log.credits_after} remaining</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
